/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import Breadcrumb from "../../components/Breadcrumb";
import makeRequest from "../../makeRequest";
import { PatientAuthContext } from "../../contexts/PatientAuthContext";

/* ─── Injected styles ──────────────────────────────────────────────── */
const STYLE_ID = "my-reports-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes mrSpin { to { transform: rotate(360deg); } }
    .mr-spinner {
      width: 44px; height: 44px; border: 4px solid #e6e9f0;
      border-top-color: #0f6fbf; border-radius: 50%;
      animation: mrSpin .8s linear infinite; margin: 60px auto; display: block;
    }
    .mr-card {
      background: #fff; border-radius: 14px; border: 1px solid #e6e9f0;
      padding: 22px 24px; margin-bottom: 16px;
      box-shadow: 0 2px 12px rgba(15,111,191,.06);
      display: flex; align-items: center; gap: 20px;
      transition: box-shadow .2s;
    }
    .mr-card:hover { box-shadow: 0 4px 24px rgba(15,111,191,.13); }
    .mr-icon {
      width: 52px; height: 52px; border-radius: 12px; background: #fef3c7;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.4rem; color: #d97706; flex-shrink: 0;
    }
    .mr-info { flex: 1; min-width: 0; }
    .mr-title {
      font-size: 1rem; font-weight: 700; color: #1a2744;
      margin: 0 0 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .mr-meta { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 6px; }
    .mr-meta-item { display: flex; align-items: center; gap: 6px; font-size: .8rem; color: #6b7280; }
    .mr-meta-item i { color: #0f6fbf; font-size: .8rem; width: 14px; text-align: center; }
    .mr-btn-view {
      flex-shrink: 0; padding: 9px 20px; border-radius: 8px;
      background: #0f6fbf; color: #fff; border: none;
      font-size: .875rem; font-weight: 600; cursor: pointer;
      display: flex; align-items: center; gap: 7px; transition: background .2s;
      text-decoration: none;
    }
    .mr-btn-view:hover { background: #0a5799; color: #fff; }
    /* Search bar */
    .mr-search {
      width: 100%; max-width: 360px; padding: 10px 16px 10px 40px;
      border: 1.5px solid #d1d5db; border-radius: 10px; font-size: .9rem;
      outline: none; transition: border-color .2s;
    }
    .mr-search:focus { border-color: #0f6fbf; }
    .mr-search-wrap { position: relative; display: inline-block; }
    .mr-search-wrap i { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #9ca3af; font-size: .85rem; }
    /* Pagination */
    .mr-pagination { display: flex; justify-content: center; align-items: center; gap: 6px; margin-top: 32px; flex-wrap: wrap; }
    .mr-page-btn {
      min-width: 36px; height: 36px; padding: 0 10px; border-radius: 8px;
      border: 1px solid #d1d5db; background: #fff; color: #374151;
      cursor: pointer; font-size: .875rem; transition: all .2s;
      display: flex; align-items: center; justify-content: center;
    }
    .mr-page-btn:hover { background: #e8f3fb; border-color: #0f6fbf; color: #0f6fbf; }
    .mr-page-btn.active { background: #0f6fbf; border-color: #0f6fbf; color: #fff; }
    .mr-page-btn:disabled { opacity: .4; cursor: not-allowed; }
    /* Empty */
    .mr-empty { text-align: center; padding: 64px 24px; }
    .mr-empty-icon { font-size: 3rem; color: #d1d5db; margin-bottom: 16px; }
    .mr-empty-title { font-size: 1.15rem; font-weight: 700; color: #374151; margin-bottom: 8px; }
    .mr-empty-sub   { font-size: .9rem; color: #9ca3af; }
    @media (max-width: 600px) {
      .mr-card { flex-wrap: wrap; }
      .mr-btn-view { width: 100%; justify-content: center; }
    }
  `;
  document.head.appendChild(s);
}

/* ─── Helpers ──────────────────────────────────────────────────────── */
const SERVER_PUBLIC = process.env.REACT_APP_REPORTS_SERVER_PUBLIC || "http://115.186.148.130:8888/REPORTS/PDF/";

const buildPdfUrl = (filePath, fileName) => {
  const joined = [filePath, fileName].filter(Boolean).join("/").replace(/\\/g, "/").replace(/\/+/g, "/");
  return SERVER_PUBLIC + joined;
};

const formatDate = (raw) => {
  try {
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return format(d, "MMM d, yyyy");
  } catch {
    return raw || "—";
  }
};

/* ─── Pagination ────────────────────────────────────────────────────── */
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) pages.push(i);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }
  return (
    <div className="mr-pagination">
      <button className="mr-page-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>
        <i className="fa-regular fa-chevron-left" style={{ fontSize: "0.7rem" }}></i>
      </button>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e-${i}`} style={{ padding: "0 4px", color: "#9ca3af" }}>
            …
          </span>
        ) : (
          <button key={p} className={`mr-page-btn${p === page ? " active" : ""}`} onClick={() => onPage(p)}>
            {p}
          </button>
        ),
      )}
      <button className="mr-page-btn" disabled={page === totalPages} onClick={() => onPage(page + 1)}>
        <i className="fa-regular fa-chevron-right" style={{ fontSize: "0.7rem" }}></i>
      </button>
    </div>
  );
}

/* ─── Report card ──────────────────────────────────────────────────── */
function ReportCard({ report }) {
  const pdfUrl = buildPdfUrl(report.PDF_FILE_PATH, report.PDF_FILE_NAME);
  return (
    <div className="mr-card">
      <div className="mr-icon">
        <i className="fa-regular fa-file-medical"></i>
      </div>
      <div className="mr-info">
        <p className="mr-title">{report.CPT_DESC || report.PDF_FILE_NAME || "Lab Report"}</p>
        <div className="mr-meta">
          {report.INVOICE_DATE && (
            <div className="mr-meta-item">
              <i className="fa-regular fa-calendar"></i>
              <span>{formatDate(report.INVOICE_DATE)}</span>
            </div>
          )}
          {report.ORDER_NO && (
            <div className="mr-meta-item">
              <i className="fa-regular fa-hashtag"></i>
              <span>Order# {report.ORDER_NO}</span>
            </div>
          )}
          {report.PDF_FILE_NAME && (
            <div className="mr-meta-item">
              <i className="fa-regular fa-file-pdf"></i>
              <span style={{ fontFamily: "monospace", fontSize: ".75rem" }}>{report.PDF_FILE_NAME}</span>
            </div>
          )}
        </div>
      </div>
      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="mr-btn-view">
        <i className="fa-regular fa-eye"></i>
        View Report
      </a>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────── */
const LIMIT = 10;

export default function MyReportsMain() {
  const navigate = useNavigate();
  const { patient, isLoading: authLoading } = useContext(PatientAuthContext);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");

  /* Auth guard */
  useEffect(() => {
    if (!authLoading && !patient) {
      navigate("/patient-login?redirect=/my-reports");
    }
  }, [patient, authLoading, navigate]);

  /* Fetch */
  const fetchReports = useCallback(
    async (p = 1) => {
      if (!patient) return;
      setLoading(true);
      try {
        const data = await makeRequest({
          route: `/patient/reports?page=${p}&limit=${LIMIT}`,
          method: "get",
        });
        const list = data?.reports?.data || [];
        setReports(list);
        const total = data?.reports?.total_items || 0;
        setTotalItems(total);
        setTotalPages(Math.ceil(total / LIMIT) || 1);
      } catch (err) {
        toast.error("Could not load reports. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [patient],
  );

  useEffect(() => {
    if (patient) fetchReports(page);
  }, [patient, page, fetchReports]);

  /* Client-side search filter */
  const filtered = search.trim()
    ? reports.filter((r) => [r.CPT_DESC, r.ORDER_NO, r.PDF_FILE_NAME].filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase()))
    : reports;

  if (authLoading) {
    return (
      <main>
        <Breadcrumb title="My Reports" />
        <div className="container pt-120 pb-120">
          <div className="mr-spinner"></div>
        </div>
      </main>
    );
  }

  if (!patient) return null;

  return (
    <main>
      <Breadcrumb title="My Reports" />

      <div className="container pt-80 pb-120">
        {/* Page header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 28,
          }}
        >
          <div>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, color: "#1a2744", margin: 0 }}>My Lab Reports</h2>
            <p style={{ color: "#6b7280", fontSize: ".9rem", margin: "4px 0 0" }}>
              {patient.mrno} — {patient.name || [patient.firstName, patient.lastName].filter(Boolean).join(" ")}
              {totalItems > 0 && (
                <span
                  style={{
                    marginLeft: 10,
                    background: "#e8f3fb",
                    color: "#0f6fbf",
                    padding: "2px 10px",
                    borderRadius: 20,
                    fontSize: ".78rem",
                    fontWeight: 700,
                  }}
                >
                  {totalItems} report{totalItems !== 1 ? "s" : ""}
                </span>
              )}
            </p>
          </div>

          {/* Search */}
          <div className="mr-search-wrap">
            <i className="fa-regular fa-magnifying-glass"></i>
            <input
              className="mr-search"
              type="text"
              placeholder="Search by test name or order #…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="mr-spinner"></div>
        ) : filtered.length === 0 ? (
          <div className="mr-empty">
            <div className="mr-empty-icon">
              <i className="fa-regular fa-folder-open"></i>
            </div>
            <div className="mr-empty-title">{search ? "No reports match your search" : "No lab reports found"}</div>
            <p className="mr-empty-sub">
              {search ? "Try a different search term." : "Your lab reports will appear here once they are available."}
            </p>
            {search && (
              <button
                style={{
                  marginTop: 16,
                  padding: "8px 20px",
                  borderRadius: 8,
                  background: "#f3f4f6",
                  border: "none",
                  cursor: "pointer",
                  color: "#374151",
                  fontWeight: 600,
                }}
                onClick={() => setSearch("")}
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <>
            {filtered.map((report, i) => (
              <ReportCard key={report.ORDER_NO || `${report.PDF_FILE_NAME}-${i}`} report={report} />
            ))}
            {!search && <Pagination page={page} totalPages={totalPages} onPage={(p) => setPage(p)} />}
          </>
        )}
      </div>
    </main>
  );
}
